import { useEffect, useState } from "react";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import {
  getSalesForBusinessDate,
  getShiftsForBusinessDate,
  getTendersForSales,
} from "../../data/local/repositories";
import { readCorrectionPosition } from "../../data/local/corrections";
import { businessDate } from "../../domain/time";
import { db, type LocalSale, type LocalShift, type SaleStatus } from "../../data/local/db";
import { Slot } from "../slots/Slot";

/**
 * Finding the sale to correct (T162).
 *
 * Reads from the LOCAL store only. A refund at the counter cannot wait on a
 * network round trip, and the terminal already holds every sale it rang.
 *
 * SCOPED TO ONE SHIFT, DEFAULTING TO THE ONE CURRENTLY OPEN. A day-scoped
 * list mixes the takings of everyone who ran the till that day: the person
 * who opened at six and cashed out at two sees her sales interleaved with
 * the closing shift's, which is exactly the wrong list to be scanning when
 * checking your own drawer. The whole day is still one option away — the
 * shift picker offers every shift that traded on the selected date plus
 * "All shifts" — it just is not what a fresh open of this screen shows.
 *
 * The status shown here is derived from the correction records, not read off
 * the sale row — the sale is append-only, so its stored status never changes
 * (FR-021). Deriving it in one place is what keeps this list from disagreeing
 * with the books.
 */

interface RecentSalesProps {
  currency: CurrencyFormat;
  timezone: string;
  /** The shift open on this terminal. The list opens scoped to it. */
  shiftId: string;
  tenderLabels: Record<string, string>;
  onSelect(saleId: string): void;
  onClose(): void;
}

interface Row {
  sale: LocalSale;
  status: SaleStatus;
  /** Payment method as shown: one label, or the split joined with "+". */
  payment: string;
}

/** Sentinel for the shift picker's "don't scope to a shift" option. */
const ALL_SHIFTS = "all";

export function RecentSales({
  currency,
  timezone,
  shiftId,
  tenderLabels,
  onSelect,
  onClose,
}: RecentSalesProps) {
  const today = businessDate(new Date(), timezone);
  const [date, setDate] = useState(today);
  const [shiftFilter, setShiftFilter] = useState<string>(shiftId);
  const [shifts, setShifts] = useState<ShiftOption[]>([]);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [query, setQuery] = useState("");

  // The shifts that traded on the selected date, for the picker. Looking back
  // at another day drops the open-shift scope — that shift did not trade then
  // — so the list falls back to the whole day rather than showing nothing.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const options = await describeShifts(date, currency.locale, shiftId);
      if (cancelled) return;
      setShifts(options);
      setShiftFilter((current) =>
        options.some((option) => option.id === current) ? current : ALL_SHIFTS,
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [date, currency.locale, shiftId]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);

    void (async () => {
      const sales = await getSalesForBusinessDate(date);
      const tenders = await getTendersForSales(sales.map((sale) => sale.id));
      const withStatus = await Promise.all(
        sales.map(async (sale) => ({
          sale,
          status: (await readCorrectionPosition(sale.id)).status,
          payment: describePayment(tenders.get(sale.id) ?? [], tenderLabels),
        })),
      );
      if (!cancelled) setRows(withStatus);
    })();

    return () => {
      cancelled = true;
    };
  }, [date, tenderLabels]);

  const term = query.trim().toLowerCase();
  const filtered = (rows ?? []).filter(
    (row) =>
      (shiftFilter === ALL_SHIFTS || row.sale.shiftId === shiftFilter) &&
      (term === "" || row.sale.documentNumber.toLowerCase().includes(term)),
  );

  return (
    <div className="recent" role="dialog" aria-label="Recent sales">
      <header className="recent__head">
        <h2 className="recent__title">Recent sales</h2>
        <button type="button" className="recent__close" onClick={onClose}>
          Close
        </button>
      </header>

      <div className="recent__filters">
        <input
          className="recent__date"
          type="date"
          value={date}
          max={today}
          onChange={(event) => setDate(event.target.value || today)}
        />
        {date !== today && (
          <button type="button" className="recent__today" onClick={() => setDate(today)}>
            Today
          </button>
        )}
        <select
          className="recent__shift"
          aria-label="Shift"
          value={shiftFilter}
          onChange={(event) => setShiftFilter(event.target.value)}
        >
          {shifts.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
          <option value={ALL_SHIFTS}>All shifts</option>
        </select>
        <input
          className="recent__search"
          placeholder="Document number"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          // The keypad is the right keyboard here: document numbers are digits
          // with a fixed prefix, and staff are on a tablet with no keyboard.
          inputMode="numeric"
        />
      </div>

      {rows === null ? (
        <p className="recent__empty">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="recent__empty">{emptyMessage(term, date === today, shiftFilter)}</p>
      ) : (
        <ul className="recent__list">
          {filtered.map(({ sale, status, payment }) => (
            <li key={sale.id}>
              <button
                type="button"
                className={`recent__row is-${status}`}
                onClick={() => onSelect(sale.id)}
              >
                <span className="recent__doc">{sale.documentNumber}</span>
                <span className="recent__time">
                  {new Date(sale.createdAt).toLocaleTimeString(currency.locale, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span className="recent__pay">{payment}</span>
                <span className="recent__amount money">{formatMoney(sale.total, currency)}</span>
                {status !== "finalized" && (
                  <span className="recent__status">{STATUS_LABELS[status]}</span>
                )}
              </button>
              {/* Beside the row, never inside it: a button inside a button is
                  not a thing a touch screen can tap reliably. */}
              <Slot
                id="till.recentSale.actions"
                saleId={sale.id}
                shiftId={sale.shiftId}
                shiftOpen={sale.shiftId === shiftId}
                status={status}
                loadLines={() => loadSaleLinesForModules(sale.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A sale's lines in the shape `sale.finalized` delivers them, for a module
 * acting on a sale after the fact (contracts/core-extensions.md §4). Loaded on
 * demand rather than for every row of the list.
 */
async function loadSaleLinesForModules(saleId: string) {
  const [lines, discounts] = await Promise.all([
    db.saleLines.where("saleId").equals(saleId).sortBy("lineNumber"),
    db.discounts.where("saleId").equals(saleId).toArray(),
  ]);
  const grantedBy = new Map(
    discounts
      .filter((discount) => discount.grantedByModule && discount.saleLineId)
      .map((discount) => [discount.saleLineId as string, discount.grantedByModule as string]),
  );
  return lines.map((line) => ({
    lineId: line.id,
    menuItemId: line.menuItemId,
    variantId: line.variantId,
    modifierIds: line.modifiers.map((modifier) => modifier.modifierId),
    quantity: line.quantity,
    note: line.note,
    lineTotal: line.lineTotal,
    grantedByModule: grantedBy.get(line.id) ?? null,
  }));
}

interface ShiftOption {
  id: string;
  label: string;
}

/**
 * The shift picker's labels: the hours it traded, because "shift 2" means
 * nothing to the person trying to find her own sales.
 *
 * A NAME IS ONLY SHOWN WHERE IT IS ACTUALLY KNOWN. `db.staff` replicates the
 * authorizing managers, not every cashier (see staff-cache.ts — that is a
 * deliberate limit, not an oversight), so naming every shift would mean
 * labelling most of them "Staff" — noise that makes two shifts look alike,
 * which is the exact confusion this picker exists to remove. The open shift
 * says so instead, and the hours tell the rest apart.
 */
async function describeShifts(
  date: string,
  locale: string,
  currentShiftId: string,
): Promise<ShiftOption[]> {
  const shifts = await getShiftsForBusinessDate(date);
  const names = new Map<string, string | undefined>();
  for (const shift of shifts) {
    if (names.has(shift.openedBy)) continue;
    names.set(shift.openedBy, (await db.staff.get(shift.openedBy))?.name);
  }

  return shifts.map((shift) => {
    const hours = shiftHours(shift, locale);
    const who = shift.id === currentShiftId ? "This shift" : names.get(shift.openedBy);
    return { id: shift.id, label: who ? `${who} · ${hours}` : hours };
  });
}

function shiftHours(shift: LocalShift, locale: string): string {
  const opened = clockTime(shift.openedAt, locale);
  return shift.closedAt ? `${opened}–${clockTime(shift.closedAt, locale)}` : `${opened}–now`;
}

function clockTime(timestamp: string, locale: string): string {
  return new Date(timestamp).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/**
 * Payment method for the list. A split shows both methods rather than a bare
 * "Split" — which one covered the balance is the thing being looked for when
 * someone comes back to the counter about a card charge.
 */
function describePayment(
  tenders: { type: string }[],
  tenderLabels: Record<string, string>,
): string {
  const types: string[] = [];
  for (const tender of tenders) {
    if (!types.includes(tender.type)) types.push(tender.type);
  }
  return types.map((type) => tenderLabels[type] ?? type).join(" + ");
}

function emptyMessage(term: string, isToday: boolean, shiftFilter: string): string {
  const scope = shiftFilter === ALL_SHIFTS ? "on this date" : "in this shift";
  if (term !== "") return `No sale with that number ${scope}.`;
  if (shiftFilter !== ALL_SHIFTS) return "No sales in this shift.";
  return isToday ? "No sales yet today." : "No sales on this date.";
}

const STATUS_LABELS: Record<SaleStatus, string> = {
  finalized: "",
  voided: "Voided",
  refunded: "Refunded",
  partially_refunded: "Part refunded",
};

export default RecentSales;
